import { useState, useCallback, useEffect, useMemo } from 'react'
import { z } from 'zod'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { RoomTypeBadge } from '@/components/rooms/RoomTypeBadge'
import * as bookingService from '@/services/bookingService'
import * as roomService from '@/services/roomService'
import * as promoService from '@/services/promoService'
import * as authService from '@/services/authService'
import * as settingsService from '@/services/settingsService'
import * as blockService from '@/services/blockService'
import { Check, Clock, AlertCircle } from 'lucide-react'
import type { PromoCode } from '@/types/promo'
import type { Booking, BookingStatus, InternalTag, Room, RoomType, BookingMode } from '@/types/schedule'
import { getRoomPriceConfig } from '@/types/schedule'
import { calculateBookingPrice, formatDate, formatDateInput, formatPrice } from '@/utils/helpers'

interface BookingModalProps {
  open: boolean
  onClose: () => void
  mode: 'create' | 'edit'
  booking?: Booking
  prefillRoomId?: string
  prefillDate?: string
  prefillStartTime?: string
  prefillEndTime?: string
  prefillMode?: BookingMode
  prefillBookingSlots?: string[]
  onSave: (booking: Omit<Booking, 'id'> | Booking) => void
  onDelete?: (id: string) => void
}

const INTERNAL_TAGS: { value: InternalTag; label: string; icon: string }[] = [
  { value: 'cleaning', label: 'Dọn phòng', icon: '🧹' },
  { value: 'maintenance', label: 'Bảo trì', icon: '🔧' },
  { value: 'locked', label: 'Khóa phòng', icon: '🚫' },
  { value: 'custom', label: 'Tùy chỉnh', icon: '📝' },
]

const STATUS_OPTIONS: { value: BookingStatus; label: string }[] = [
  { value: 'pending', label: 'Chờ xác nhận' },
  { value: 'confirmed', label: 'Đã xác nhận' },
  { value: 'checked-in', label: 'Đã nhận phòng' },
  { value: 'checked-out', label: 'Đã trả phòng' },
  { value: 'cancelled', label: 'Đã hủy' },
]

const guestSchema = z.object({
  guestName: z.string().min(1, 'Tên khách hàng là bắt buộc'),
  guestPhone: z
    .string()
    .min(1, 'Số điện thoại là bắt buộc')
    .regex(/^0\d{9,10}$/, 'Số điện thoại không hợp lệ (VD: 0901234567)'),
  startTime: z.string().min(1),
  endTime: z.string().min(1),
})

const internalSchema = z.object({
  startTime: z.string().min(1),
  endTime: z.string().min(1),
  internalTag: z.enum(['cleaning', 'maintenance', 'locked', 'custom']),
})

interface TimeSelectsProps {
  field: 'startTime' | 'endTime'
  time: string
  onTimeChange: (field: 'startTime' | 'endTime', value: string) => void
  disabled?: boolean
}

const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: String(i).padStart(2, "0") + "h",
  label: String(i).padStart(2, "0") + "h",
}))

const MINUTES = [
  { value: "00", label: "00" },
  { value: "15", label: "15" },
  { value: "30", label: "30" },
  { value: "45", label: "45" },
]

function TimeSelects({ field, time, onTimeChange, disabled }: TimeSelectsProps) {
  const currentHour = time.split(":")[0] || "08"
  const currentMinute = time.split(":")[1] || "00"
  
  return (
    <div className="flex gap-1.5 items-center">
      <Select
        value={currentHour + "h"}
        disabled={disabled}
        onValueChange={(v) => onTimeChange(field, `${v.replace("h", "")}:${currentMinute}`)}
      >
        <SelectTrigger className="w-[66px] px-1.5 shrink-0 bg-white">
          <SelectValue />
        </SelectTrigger>
        <SelectContent position="popper" align="start">
          {HOURS.map((h) => (
            <SelectItem key={h.value} value={h.value}>
              {h.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={currentMinute}
        disabled={disabled}
        onValueChange={(v) => onTimeChange(field, `${currentHour}:${v}`)}
      >
        <SelectTrigger className="w-[58px] px-1.5 shrink-0 bg-white">
          <SelectValue />
        </SelectTrigger>
        <SelectContent position="popper" align="start">
          {MINUTES.map((m) => (
            <SelectItem key={m.value} value={m.value}>
              {m.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

/**
 * Modal tạo/chỉnh sửa booking — hai tab: khách hàng (guest) và nội bộ (internal)
 * Validate với Zod, kiểm tra trùng lịch, áp dụng mã khuyến mãi
 */
export function BookingModal({
  open,
  onClose,
  mode,
  booking,
  prefillRoomId,
  prefillDate,
  prefillStartTime,
  prefillEndTime,
  prefillMode,
  prefillBookingSlots,
  onSave,
  onDelete,
}: BookingModalProps) {
  const isAdmin = authService.isAdmin()
  const isEdit = mode === 'edit'

  const [rooms, setRooms] = useState<Room[]>([])
  useEffect(() => {
    roomService.getAll().then((data) => {
      setRooms(data.map((r) => ({
        id: r.id, name: r.name, type: r.type,
        amenities: r.amenities || [],
        hourlyRate: r.hourlyRate, dailyRate: r.dailyRate,
        overnightRate: r.overnightRate, extraHourRate: r.extraHourRate,
        combo3hRate: r.combo3hRate, combo6h1hRate: r.combo6h1hRate,
        combo6h1hDiscount: r.combo6h1hDiscount,
        bookingMode: r.bookingMode,
        slotRates: r.slotRates,
      })))
    }).catch(() => {})
  }, [])

  const [activeTab, setActiveTab] = useState<'guest' | 'internal'>(
    booking?.category === 'internal' ? 'internal' : 'guest'
  )

  const [roomId, setRoomId] = useState(booking?.roomId || prefillRoomId || '')

  useEffect(() => {
    if (!roomId && rooms.length > 0) setRoomId(rooms[0].id)
  }, [rooms, roomId])
  const [date, setDate] = useState(
    booking?.date || prefillDate || formatDateInput(new Date())
  )
  const [startTime, setStartTime] = useState(booking?.startTime || prefillStartTime || '08:00')
  const [endTime, setEndTime] = useState(booking?.endTime || prefillEndTime || '10:00')

  const handleTimeChange = useCallback((field: 'startTime' | 'endTime', value: string) => {
    if (field === 'startTime') {
      setStartTime(value)
    } else {
      setEndTime(value)
    }
  }, [])

  const [bookingMode, setBookingMode] = useState<BookingMode>(
    booking?.mode || prefillMode || 'hourly'
  )
  const [bookingSlots, setBookingSlots] = useState<string[]>(
    booking?.bookingSlots || prefillBookingSlots || []
  )
  const [globalSlots, setGlobalSlots] = useState<settingsService.FixedSlot[]>([])

  useEffect(() => {
    settingsService.getFixedSlots().then((data) => {
      setGlobalSlots(data)
    }).catch(() => {})
  }, [])

  const [blockDefinitions, setBlockDefinitions] = useState<blockService.BlockDefinition[]>([])

  const activeRoom = useMemo(() => rooms.find((r) => r.id === roomId), [rooms, roomId])

  useEffect(() => {
    if (activeRoom) {
      blockService.getByRoomType(activeRoom.type).then((data) => {
        setBlockDefinitions(data)
        if (activeRoom.bookingMode === 'block' && data.length === 0) {
          setBookingMode('hourly')
          toast.warning(`Hạng phòng ${activeRoom.type.toUpperCase()} chưa có cấu hình ca. Đã chuyển sang đặt theo giờ.`)
        }
      }).catch(() => {})
    }
  }, [activeRoom])

  useEffect(() => {
    if (activeRoom) {
      if (activeRoom.bookingMode === 'block' && blockDefinitions.length > 0) {
        setBookingMode('block')
      } else if (bookingMode === 'block') {
        setBookingMode('hourly')
      }
    }
  }, [activeRoom, bookingMode, blockDefinitions])

  const [guestName, setGuestName] = useState(booking?.guestName || '')
  const [guestPhone, setGuestPhone] = useState(booking?.guestPhone || '')
  const [status, setStatus] = useState<BookingStatus>(booking?.status || 'pending')
  const [voucher, setVoucher] = useState(booking?.voucher || '')
  const [note, setNote] = useState(booking?.note || '')

  const [internalTag, setInternalTag] = useState<InternalTag>(
    booking?.internalTag || 'cleaning'
  )
  const [internalNote, setInternalNote] = useState(booking?.internalNote || '')

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [voucherStatus, setVoucherStatus] = useState<{
    valid: boolean
    message: string
  } | null>(null)
  const [validatedPromo, setValidatedPromo] = useState<PromoCode | null>(null)
  const [validatingVoucher, setValidatingVoucher] = useState(false)

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  useEffect(() => {
    if (open) {
      setRoomId(booking?.roomId || prefillRoomId || '')
      setDate(booking?.date || prefillDate || formatDateInput(new Date()))
      setStartTime(booking?.startTime || prefillStartTime || '08:00')
      setEndTime(booking?.endTime || prefillEndTime || '10:00')
      setBookingMode(booking?.mode || prefillMode || 'hourly')
      setBookingSlots(booking?.bookingSlots || prefillBookingSlots || [])
      setGuestName(booking?.guestName || '')
      setGuestPhone(booking?.guestPhone || '')
      setStatus(booking?.status || 'pending')
      setVoucher(booking?.voucher || '')
      setNote(booking?.note || '')
      setInternalTag(booking?.internalTag || 'cleaning')
      setInternalNote(booking?.internalNote || '')
      setErrors({})
      setVoucherStatus(null)
      setValidatedPromo(null)
    }
  }, [open, booking, prefillRoomId, prefillDate, prefillStartTime, prefillEndTime, prefillMode, prefillBookingSlots])

  const syncTimesFromSlots = useCallback((slotsList: string[]) => {
    if (slotsList.length === 0 || globalSlots.length === 0) return

    const selectedDefs = globalSlots.filter((s) => slotsList.includes(s.id))
    if (selectedDefs.length === 0) return

    let minMinutes = Infinity
    let maxMinutes = -Infinity
    let bestStartStr = ''
    let bestEndStr = ''

    selectedDefs.forEach((s) => {
      const startMin = timeToMinutes(s.startTime)
      let endMin = timeToMinutes(s.endTime)
      if (endMin <= startMin) endMin += 24 * 60

      if (startMin < minMinutes) {
        minMinutes = startMin
        bestStartStr = s.startTime
      }
      if (endMin > maxMinutes) {
        maxMinutes = endMin
        bestEndStr = s.endTime
      }
    })

    if (bestStartStr) setStartTime(bestStartStr)
    if (bestEndStr) setEndTime(bestEndStr)
  }, [globalSlots])

  const syncTimesFromBlocks = useCallback((slotsList: string[]) => {
    if (slotsList.length === 0 || blockDefinitions.length === 0) return

    const sortedAvailable = [...blockDefinitions].sort((a, b) => a.sortOrder - b.sortOrder)
    const selectedDefs = sortedAvailable.filter((b) => slotsList.includes(b.id))
    if (selectedDefs.length === 0) return

    setStartTime(selectedDefs[0].startTime)
    setEndTime(selectedDefs[selectedDefs.length - 1].endTime)
  }, [blockDefinitions])

  useEffect(() => {
    if (bookingMode === 'slots') {
      syncTimesFromSlots(bookingSlots)
    } else if (bookingMode === 'block') {
      syncTimesFromBlocks(bookingSlots)
    }
  }, [bookingSlots, globalSlots, blockDefinitions, bookingMode, syncTimesFromSlots, syncTimesFromBlocks])

  const validateVoucher = useCallback(async () => {
    if (!voucher.trim()) {
      setVoucherStatus(null)
      setValidatedPromo(null)
      return
    }
    const room = rooms.find((r) => r.id === roomId)
    const roomType: RoomType = room?.type || 'standard'
    setValidatingVoucher(true)
    try {
      const result = await promoService.validate(voucher.trim().toUpperCase(), roomType)
      if (result.valid && result.promo) {
        const discountText =
          result.promo.discountType === 'percent'
            ? `${result.promo.discountValue}%`
            : `${result.promo.discountValue.toLocaleString('vi-VN')}d`
        setVoucherStatus({ valid: true, message: `Giảm ${discountText}` })
        setValidatedPromo(result.promo)
      } else {
        setVoucherStatus({ valid: false, message: result.error || 'Mã không hợp lệ' })
        setValidatedPromo(null)
      }
    } catch (err) {
      setVoucherStatus({ valid: false, message: err instanceof Error ? err.message : 'Không kiểm tra được mã' })
      setValidatedPromo(null)
    } finally {
      setValidatingVoucher(false)
    }
  }, [voucher, roomId, rooms])

  const timeToMinutes = (time: string): number => {
    const [h, m] = time.split(':').map(Number)
    return h * 60 + m
  }

  const handleSave = async () => {
    const newErrors: Record<string, string> = {}

    // `endTime === startTime` = zero-duration → invalid.
    // `endTime < startTime` = overnight (cross-midnight) → valid, BE sẽ validate overlap.
    if (timeToMinutes(endTime) === timeToMinutes(startTime)) {
      newErrors.endTime = 'Giờ kết thúc không được trùng giờ bắt đầu'
    }

    if (activeTab === 'guest') {
      const result = guestSchema.safeParse({ guestName, guestPhone, startTime, endTime })
      if (!result.success) {
        for (const issue of result.error.issues) {
          const field = issue.path[0] as string
          newErrors[field] = issue.message
        }
      }
    } else {
      const result = internalSchema.safeParse({ startTime, endTime, internalTag })
      if (!result.success) {
        for (const issue of result.error.issues) {
          const field = issue.path[0] as string
          newErrors[field] = issue.message
        }
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    const excludeId = isEdit && booking ? booking.id : undefined
    try {
      const { hasConflict, conflicts } = await bookingService.checkConflicts(roomId, date, startTime, endTime, excludeId)
      if (hasConflict && conflicts && conflicts.length > 0) {
        const hasInternalOrOccupied = conflicts.some(
          (c) => c.category === 'internal' || c.status === 'checked-in' || c.status === 'checked-out'
        )
        if (hasInternalOrOccupied) {
          toast.error('Trùng lịch! Khung giờ này xung đột với lịch bảo trì hoặc phòng đang hoạt động.')
          return
        }

        const guestNames = conflicts
          .map((c) => c.guestName || c.guestPhone || 'Khách vãng lai')
          .filter(Boolean)
          .join(', ')
        const confirmOverride = window.confirm(
          `Khung giờ này trùng với lịch đặt của khách [${guestNames}]. Bạn có chắc chắn muốn ghi đè và tự động hủy lịch đặt của khách này không?`
        )
        if (!confirmOverride) {
          return
        }
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Không kiểm tra được trùng lịch')
      return
    }


    const isGuest = activeTab === 'guest'

    const bookingData: Omit<Booking, 'id'> = {
      roomId,
      date,
      startTime,
      endTime,
      status: isGuest ? status : 'confirmed',
      totalPrice: isGuest ? calculatePrice() : 0,
      category: isGuest ? 'guest' : 'internal',
      mode: isGuest ? bookingMode : 'hourly',
      bookingSlots: isGuest ? bookingSlots : [],
      ...(isGuest && {
        guestName,
        guestPhone,
        note,
        voucher: voucher.trim().toUpperCase() || undefined,
      }),
      ...(!isGuest && {
        internalTag,
        internalNote: internalNote || undefined,
        createdBy: authService.getAuth().userName,
      }),
    }

    if (isEdit && booking) {
      onSave({ ...bookingData, id: booking.id } as Booking)
    } else {
      onSave(bookingData)
    }

    onClose()
  }

  const calculatePrice = (): number => {
    const room = rooms.find((r) => r.id === roomId)
    if (!room) return 0
    const priceConfig = getRoomPriceConfig(room)
    
    const startMin = timeToMinutes(startTime)
    let endMin = timeToMinutes(endTime)
    if (endMin <= startMin) endMin += 24 * 60
    const hours = (endMin - startMin) / 60
    
    let price = calculateBookingPrice(
      bookingMode,
      hours,
      { ...priceConfig, globalSlots, blockRates: blockDefinitions },
      'bonus_hour',
      { startTime, endTime },
      bookingSlots
    )

    if (voucher.trim() && validatedPromo && validatedPromo.code === voucher.trim().toUpperCase()) {
      if (validatedPromo.discountType === 'percent') {
        price = Math.round(price * (1 - validatedPromo.discountValue / 100))
      } else {
        price = Math.max(0, price - validatedPromo.discountValue)
      }
    }

    return price
  }

  const handleDelete = () => {
    if (booking && onDelete) {
      onDelete(booking.id)
      setShowDeleteConfirm(false)
      onClose()
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <DialogContent className="sm:max-w-[520px] max-h-[90vh] flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle>
              {isEdit ? 'Chỉnh sửa booking' : 'Tạo booking mới'}
            </DialogTitle>
            <DialogDescription>
              {isEdit
                ? 'Cập nhật thông tin booking hiện tại'
                : 'Điền thông tin để tạo booking mới'}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto pr-1 py-1 -mr-1">
            <Tabs
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as 'guest' | 'internal')}
          >
            <TabsList className="w-full">
              <TabsTrigger value="guest" className="flex-1">
                Khách hàng
              </TabsTrigger>
              <TabsTrigger value="internal" className="flex-1">
                Nội bộ
              </TabsTrigger>
            </TabsList>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label htmlFor="room">Phòng</Label>
                <Select value={roomId} onValueChange={setRoomId}>
                  <SelectTrigger className="mt-1 w-full bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {rooms.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        <span className="inline-flex items-center gap-2">
                          <span>{r.name}</span>
                          <RoomTypeBadge type={r.type} size="sm" />
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {activeTab === 'guest' && (
                <div className="col-span-2">
                  <Label htmlFor="bookingMode">Hình thức đặt</Label>
                  {activeRoom?.bookingMode === 'block' && blockDefinitions && blockDefinitions.length > 0 ? (
                    <Select value="block" disabled>
                      <SelectTrigger className="mt-1 w-full bg-gray-50 text-gray-700">
                        <SelectValue placeholder="Đặt theo ca cố định (Block)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="block">Đặt theo ca cố định (Block)</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Select value={bookingMode} onValueChange={(v) => {
                      const m = v as BookingMode
                      if (m === 'block' && blockDefinitions.length === 0) {
                        const roomTypeLabel = activeRoom?.type === 'supervip' ? 'Super VIP' : (activeRoom?.type === 'vip' ? 'VIP' : 'Tiêu chuẩn')
                        toast.error(`Hạng phòng ${roomTypeLabel} chưa được cấu hình ca đặt phòng nào. Hãy thêm cấu hình ca cho hạng phòng này trước.`)
                        return
                      }
                      setBookingMode(m)
                      if (m !== 'slots') {
                        setBookingSlots([])
                      } else if (bookingSlots.length === 0 && globalSlots.length > 0) {
                        setBookingSlots([globalSlots[0].id])
                      }
                    }}>
                      <SelectTrigger className="mt-1 w-full bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hourly">Theo giờ</SelectItem>
                        <SelectItem value="daily">Theo ngày</SelectItem>
                        <SelectItem value="overnight">Qua đêm</SelectItem>
                        <SelectItem value="combo3h">Combo 3H</SelectItem>
                        <SelectItem value="combo6h1h">Combo 6H+1H</SelectItem>
                        <SelectItem value="slots">Khung giờ cố định (Slots)</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}

              {activeTab === 'guest' && bookingMode === 'slots' && (
                <div className="col-span-2 border p-3 rounded-lg bg-emerald-50/30 border-emerald-100 space-y-2">
                  <Label className="text-emerald-800 font-semibold">Chọn Khung Giờ Cố Định (Chọn nhiều để tạo combo)</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1.5">
                    {globalSlots.map((slot) => {
                      const isChecked = bookingSlots.includes(slot.id)
                      return (
                        <label key={slot.id} className={cn(
                          "flex items-center gap-2 border px-3 py-2 rounded-md cursor-pointer transition-all text-sm",
                          isChecked ? "border-emerald-600 bg-emerald-50 text-emerald-900 font-medium" : "border-gray-200 bg-white hover:bg-gray-50 text-gray-700"
                        )}>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setBookingSlots([...bookingSlots, slot.id])
                              } else {
                                setBookingSlots(bookingSlots.filter(id => id !== slot.id))
                              }
                            }}
                            className="accent-emerald-600 rounded"
                          />
                          <div className="flex flex-col">
                            <span>{slot.name}</span>
                            <span className="text-xs text-gray-500">{slot.startTime} - {slot.endTime}</span>
                          </div>
                        </label>
                      )
                    })}
                  </div>
                  {bookingSlots.length >= 2 && (
                    <p className="text-xs text-emerald-600 font-semibold animate-pulse">
                      ⚡ Combo {bookingSlots.length} slot hoạt động! Áp dụng giảm giá {bookingSlots.length === 2 ? '10%' : bookingSlots.length === 3 ? '15%' : '20%'}!
                    </p>
                  )}
                </div>
              )}

              {activeTab === 'guest' && bookingMode === 'block' && blockDefinitions && blockDefinitions.length > 0 && (
                <div className="col-span-2 border p-3 rounded-lg bg-zinc-50 border-zinc-100 dark:bg-zinc-900/50 space-y-2">
                  <Label className="text-zinc-800 dark:text-zinc-200 font-semibold flex items-center gap-1.5">
                    <Clock className="h-4 w-4 text-zinc-600" /> Chọn Ca Đặt Phòng (Chọn các ca liên tiếp)
                  </Label>
                  <div className="grid grid-cols-2 gap-2 mt-1.5">
                    {blockDefinitions.map((block) => {
                      const isChecked = bookingSlots.includes(block.id)
                      return (
                        <button
                          key={block.id}
                          type="button"
                          onClick={() => {
                            const sortedAvailable = [...blockDefinitions].sort((a, b) => a.sortOrder - b.sortOrder)
                            let nextSlots = [...bookingSlots]
                            
                            if (nextSlots.includes(block.id)) {
                              nextSlots = nextSlots.filter(id => id !== block.id)
                              const selectedDefs = sortedAvailable.filter(b => nextSlots.includes(b.id))
                              if (selectedDefs.length > 0) {
                                let isConsecutive = true
                                for (let i = 0; i < selectedDefs.length - 1; i++) {
                                  const idx1 = sortedAvailable.findIndex(b => b.id === selectedDefs[i].id)
                                  const idx2 = sortedAvailable.findIndex(b => b.id === selectedDefs[i+1].id)
                                  if (idx2 - idx1 !== 1) {
                                    isConsecutive = false
                                    break
                                  }
                                }
                                if (!isConsecutive) {
                                  nextSlots = []
                                }
                              }
                            } else {
                              if (nextSlots.length === 0) {
                                nextSlots = [block.id]
                              } else {
                                const selectedDefs = sortedAvailable.filter(b => nextSlots.includes(b.id))
                                const firstIdx = sortedAvailable.findIndex(b => b.id === selectedDefs[0].id)
                                const lastIdx = sortedAvailable.findIndex(b => b.id === selectedDefs[selectedDefs.length - 1].id)
                                const clickedIdx = sortedAvailable.findIndex(b => b.id === block.id)
                                
                                if (clickedIdx === firstIdx - 1) {
                                  nextSlots = [block.id, ...nextSlots]
                                } else if (clickedIdx === lastIdx + 1) {
                                  nextSlots = [...nextSlots, block.id]
                                } else {
                                  nextSlots = [block.id]
                                }
                              }
                            }
                            setBookingSlots(nextSlots)
                          }}
                          className={cn(
                            "flex flex-col items-start justify-between border p-2.5 rounded-lg cursor-pointer transition-all text-sm h-20 text-left",
                            isChecked
                              ? "border-zinc-900 bg-zinc-100 dark:border-zinc-50 dark:bg-zinc-800 ring-1 ring-zinc-900 dark:ring-zinc-50 text-zinc-950 dark:text-zinc-50 font-semibold"
                              : "border-gray-200 bg-white hover:bg-gray-50 text-gray-700"
                          )}
                        >
                          <div className="flex justify-between items-start w-full">
                            <span className="font-bold uppercase text-xs">{block.name}</span>
                            {isChecked && <Check className="h-3.5 w-3.5 text-zinc-900 dark:text-zinc-50 shrink-0" />}
                          </div>
                          <div className="flex justify-between items-end w-full mt-2">
                            <span className="text-[11px] font-mono text-gray-500">{block.startTime} - {block.endTime}</span>
                            <span className="text-xs font-semibold text-zinc-900 dark:text-zinc-100">{formatPrice(block.price)}đ</span>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                  {bookingSlots.length > 0 && (
                    <div className="mt-1 flex items-center gap-1 text-xs text-zinc-700 dark:text-zinc-300 font-medium">
                      <AlertCircle className="h-3.5 w-3.5 text-zinc-500" />
                      <span>Đã gộp ca: {startTime} - {endTime}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="col-span-2">
                <Label htmlFor="date">Ngày</Label>
                <Input
                  id="date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="mt-1 bg-white"
                />
              </div>

              <div className="col-span-2 grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="startTime">Bắt đầu</Label>
                  <div className="mt-1">
                    <TimeSelects
                      field="startTime"
                      time={startTime}
                      onTimeChange={handleTimeChange}
                      disabled={activeTab === 'guest' && (bookingMode === 'slots' || bookingMode === 'block')}
                    />
                  </div>
                  {errors.startTime && (
                    <p className="text-xs text-status-error-foreground mt-0.5">{errors.startTime}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="endTime">Kết thúc</Label>
                  <div className="mt-1">
                    <TimeSelects
                      field="endTime"
                      time={endTime}
                      onTimeChange={handleTimeChange}
                      disabled={activeTab === 'guest' && (bookingMode === 'slots' || bookingMode === 'block')}
                    />
                  </div>
                  {errors.endTime && (
                    <p className="text-xs text-status-error-foreground mt-0.5">{errors.endTime}</p>
                  )}
                  {(() => {
                    const startMin = timeToMinutes(startTime)
                    const endMin = timeToMinutes(endTime)
                    if (endMin >= startMin) return null
                    const d = new Date(date + 'T00:00:00')
                    if (isNaN(d.getTime())) return null
                    d.setDate(d.getDate() + 1)
                    return (
                      <p className="text-xs text-amber-600 mt-0.5">
                        Qua đêm — kết thúc {formatDate(d)}
                      </p>
                    )
                  })()}
                </div>
              </div>
            </div>

            <TabsContent value="guest" className="mt-3 flex flex-col gap-3">
              <div>
                <Label htmlFor="guestName">Tên khách hàng</Label>
                <Input
                  id="guestName"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  placeholder="Nguyễn Văn A"
                  className="mt-1"
                />
                {errors.guestName && (
                  <p className="text-xs text-status-error-foreground mt-0.5">{errors.guestName}</p>
                )}
              </div>

              <div>
                <Label htmlFor="guestPhone">Số điện thoại</Label>
                <Input
                  id="guestPhone"
                  value={guestPhone}
                  onChange={(e) => setGuestPhone(e.target.value)}
                  placeholder="0901234567"
                  className="mt-1"
                />
                {errors.guestPhone && (
                  <p className="text-xs text-status-error-foreground mt-0.5">{errors.guestPhone}</p>
                )}
              </div>

              <div>
                <Label htmlFor="status">Trạng thái</Label>
                <Select
                  value={status}
                  onValueChange={(v) => setStatus(v as BookingStatus)}
                >
                  <SelectTrigger className="mt-1 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="voucher">Mã khuyến mãi</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    id="voucher"
                    value={voucher}
                    onChange={(e) => {
                      setVoucher(e.target.value)
                      setVoucherStatus(null)
                      setValidatedPromo(null)
                    }}
                    placeholder="SUMMER20"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={validateVoucher}
                    disabled={validatingVoucher}
                    className="shrink-0"
                  >
                    {validatingVoucher ? 'Đang kiểm tra...' : 'Kiểm tra'}
                  </Button>
                </div>
                {voucherStatus && (
                  <p
                    className={cn(
                      'text-xs mt-1',
                      voucherStatus.valid ? 'text-status-success-foreground' : 'text-status-error-foreground'
                    )}
                  >
                    {voucherStatus.valid ? '✓' : '✗'} {voucherStatus.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="note">Ghi chú</Label>
                <Textarea
                  id="note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Ghi chú thêm..."
                  rows={2}
                  className="mt-1"
                />
              </div>
            </TabsContent>

            <TabsContent value="internal" className="mt-3 flex flex-col gap-3">
              <div>
                <Label>Loại nội bộ</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {INTERNAL_TAGS.map((tag) => (
                    <button
                      key={tag.value}
                      type="button"
                      onClick={() => setInternalTag(tag.value)}
                      className={cn(
                        'flex items-center gap-2 rounded-lg border p-2.5 text-sm transition-colors',
                        internalTag === tag.value
                          ? 'border-primary bg-status-error-muted text-status-error-foreground'
                          : 'border-border hover:border-muted-foreground'
                      )}
                    >
                      <span className="text-base">{tag.icon}</span>
                      <span>{tag.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="internalNote">Ghi chú nội bộ</Label>
                <Textarea
                  id="internalNote"
                  value={internalNote}
                  onChange={(e) => setInternalNote(e.target.value)}
                  placeholder="Lý do, chi tiết..."
                  rows={2}
                  className="mt-1"
                />
              </div>
            </TabsContent>
          </Tabs>
          </div>

          <DialogFooter className="shrink-0 mt-2 gap-2">
            {isEdit && isAdmin && onDelete && (
              <Button
                variant="outline"
                className="mr-auto text-destructive border-destructive/20 hover:bg-destructive/5"
                onClick={() => setShowDeleteConfirm(true)}
              >
                Xóa
              </Button>
            )}
            <Button variant="outline" onClick={onClose}>
              Hủy
            </Button>
            <Button
              onClick={handleSave}
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              {isEdit ? 'Cập nhật' : 'Tạo mới'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Xóa booking?</AlertDialogTitle>
            <AlertDialogDescription>
              Bạn có chắc muốn xóa booking này? Hành động này không thể hoàn tác.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Hủy</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive hover:bg-destructive/90 text-white"
            >
              Xóa
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
